import {useEffect, useState} from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {useSelector} from 'react-redux';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import MDInput from 'components/MDInput';
import {ENDPOINT} from 'constants';
import MDButton from 'components/MDButton';
import {
  Card,
  CircularProgress,
  Menu,
  MenuItem,
  Grid,
  Divider,
  Icon,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import {Confirm, Loading, Notify} from 'notiflix';
import moment from 'moment';
import CountryDropdown from 'components/country/CountryDropdown';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const FILTERS = {
  status: [
    {label: 'All Status', value: 'all'},
    {label: 'Active', value: 'active'},
    {label: 'Inactive', value: 'inactive'},
    {label: 'Expired', value: 'expired'},
  ],
  scope: [
    {label: 'All Scopes', value: 'all'},
    {label: 'Global', value: 'global'},
    {label: 'Customer Specific', value: 'customer-specific'},
  ],
  type: [
    {label: 'All Types', value: 'all'},
    {label: 'Percentage', value: 'percentage'},
    {label: 'Flat Amount', value: 'flat'},
  ],
  country: [
    {label: 'All Countries', value: 'all'},
    {label: 'All Countries Scope', value: 'all-scope'},
    {label: 'Specific Country', value: 'specific'},
  ],
};

const columns = [
  {Header: '#', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Promo Code', accessor: 'code', align: 'left'},
  {Header: 'Discount', accessor: 'discount', align: 'left'},
  {Header: 'Scope', accessor: 'scope', align: 'center'},
  {Header: 'Usage', accessor: 'usageCount', align: 'center'},
  {Header: 'Expiration', accessor: 'expirationDate', align: 'center'},
  {Header: 'Status', accessor: 'status', align: 'center'},
  {Header: 'Actions', accessor: 'actions', align: 'center'},
];

const PromoCodes = () => {
  const [promos, setPromos] = useState([]);
  const [filteredPromos, setFilteredPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reRender, setReRender] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');

  const [menu, setMenu] = useState(null);
  const [selectedPromo, setSelectedPromo] = useState(null);

  const {user} = useSelector(state => state.auth);
  const navigate = useNavigate();
  const canRead = usePermission('promocodes', 'read');
  const canCreate = usePermission('promocodes', 'create');
  const canUpdate = usePermission('promocodes', 'update');
  const canDelete = usePermission('promocodes', 'delete');

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    fetchPromos();
  }, [reRender, canRead]);

  useEffect(() => {
    applyFilters();
  }, [
    promos,
    searchQuery,
    statusFilter,
    scopeFilter,
    typeFilter,
    countryFilter,
  ]);

  const fetchPromos = async () => {
    setLoading(true);
    setError(null);
    try {
      const {data} = await axios.get(`${ENDPOINT}api/v1/promo-code/get-all`, {
        headers: {Authorization: `${user?.accessToken}`},
      });
      setPromos(data?.data || []);
    } catch (error) {
      console.error('Error fetching promos:', error);
      setError('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...promos];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        p =>
          p.code?.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query),
      );
    }

    if (statusFilter !== 'all') {
      const now = moment();
      result = result.filter(p => {
        if (statusFilter === 'active')
          return p.isActive && moment(p.expirationDate).isAfter(now);
        if (statusFilter === 'inactive') return !p.isActive;
        if (statusFilter === 'expired')
          return moment(p.expirationDate).isBefore(now);
        return true;
      });
    }

    if (scopeFilter !== 'all') {
      result = result.filter(p => p.promoScope === scopeFilter);
    }

    if (typeFilter !== 'all') {
      result = result.filter(p => p.discountType === typeFilter);
    }

    if (countryFilter !== 'all') {
      result = result.filter(p => {
        if (countryFilter === 'all-scope') return p.countryScope === 'all';
        if (countryFilter === 'specific') return p.countryScope === 'specific';
        return true;
      });
    }

    setFilteredPromos(result);
  };

  const openMenu = (event, promo) => {
    setSelectedPromo(promo);
    setMenu(event.currentTarget);
  };
  const closeMenu = () => {
    setMenu(null);
    setSelectedPromo(null);
  };

  const handleToggleStatus = async () => {
    // Keep a reference before closing the menu (which clears selectedPromo)
    const promo = selectedPromo;
    closeMenu();
    try {
      Loading.circle('Changing Status...');
      await axios.patch(
        `${ENDPOINT}api/v1/promo-code/update`,
        {
          promoCodeOrId: promo.code,
          isActive: !promo.isActive,
        },
        {headers: {Authorization: `Bearer ${user?.accessToken}`}},
      );

      // Optimistically update local state immediately so the UI reflects
      // the change without waiting for the re-fetch (and without relying
      // on any stale child-component state).
      setPromos(prev =>
        prev.map(p => (p.id === promo.id ? {...p, isActive: !p.isActive} : p)),
      );

      Loading.remove();
      Notify.success(
        `Promo ${promo.isActive ? 'deactivated' : 'activated'} successfully`,
      );

      // Re-fetch from server to stay in sync with backend truth.
      setReRender(r => !r);
    } catch (error) {
      Loading.remove();
      Notify.failure('Failed to update status');
    }
  };

  const handleDelete = id => {
    const promo = promos.find(p => p.id === id);
    const usageWarning =
      promo?.usageCount > 0
        ? `\n\n⚠️ This promo has been used ${promo.usageCount} times. Deleting it will remove it permanently but usage records will be preserved.`
        : '';

    Confirm.show(
      'Delete Promo Code?',
      `Are you sure you want to delete "${promo?.code}"?${usageWarning}\n\nConsider deactivating instead if you want to keep the history.`,
      'Delete',
      'Cancel',
      async () => {
        try {
          Loading.circle('Deleting...');
          await axios.delete(`${ENDPOINT}api/v1/promo-code/delete`, {
            headers: {Authorization: `Bearer ${user?.accessToken}`},
            data: {promoCodeOrId: promo?.code || id},
          });
          Loading.remove();
          Notify.success('Promo code deleted');

          // Optimistically remove from local state right away.
          setPromos(prev => prev.filter(p => p.id !== id));

          setReRender(r => !r);
        } catch (error) {
          Loading.remove();
          Notify.failure(error?.response?.data?.message || 'Failed to delete');
        }
      },
      () => {},
    );
  };

  const getRow = (row, idx) => ({
    sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
    code: (
      <MDTypography
        variant="button"
        fontWeight="medium"
        color={canUpdate ? 'secondary' : 'dark'}
        sx={{cursor: canUpdate ? 'pointer' : 'default'}}
        onClick={canUpdate ? () => navigate(`/promocodes/edit/${row.id}`) : undefined}>
        {row.code}
      </MDTypography>
    ),
    discount: (
      <MDTypography variant="caption">
        {row.discountValue}
        {row.discountType === 'percentage' ? '%' : '₹'}
      </MDTypography>
    ),
    scope: (
      <MDBox display="flex" gap={0.5} flexDirection="column">
        <MDBadge
          badgeContent={
            row.promoScope === 'global' ? 'All Users' : 'Specific Users'
          }
          color={row.promoScope === 'global' ? 'info' : 'warning'}
          variant="gradient"
          size="sm"
        />
        <MDBadge
          badgeContent={
            row.countryScope === 'all' ? 'All Countries' : 'Specific Country'
          }
          color={row.countryScope === 'all' ? 'success' : 'error'}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    usageCount: (
      <MDTypography variant="caption">
        {row.usageCount || 0} / {row.maximumNumberOfUsage}
      </MDTypography>
    ),
    expirationDate: (
      <MDTypography variant="caption">
        {moment(row.expirationDate).format('DD MMM YYYY')}
      </MDTypography>
    ),
    status: (
      <MDBadge
        badgeContent={
          moment(row.expirationDate).isBefore(moment())
            ? 'expired'
            : row.isActive
            ? 'active'
            : 'inactive'
        }
        color={
          moment(row.expirationDate).isBefore(moment())
            ? 'error'
            : row.isActive
            ? 'success'
            : 'secondary'
        }
        variant="gradient"
        size="sm"
      />
    ),
    actions: (
      <MDBox display="flex" gap={1} justifyContent="center">
        {(canUpdate || canDelete) && (
          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={e => {
                setSelectedPromo(row);
                openMenu(e, row);
              }}>
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          </Tooltip>
        )}
      </MDBox>
    ),
  });

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to promo codes. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          mt={5}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size="3rem" color="inherit" />
            <MDTypography variant="h6" mt={2}>
              Loading promo codes...
            </MDTypography>
          </Box>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} mb={3}>
          <Card sx={{padding: 4, textAlign: 'center'}}>
            <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>
              error_outline
            </Icon>
            <MDTypography variant="h5" mb={2}>
              {error}
            </MDTypography>
            <MDButton
              variant="contained"
              onClick={() => {
                setError(null);
                fetchPromos();
              }}>
              Retry
            </MDButton>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}>
                  <MDTypography variant="h6" color="white">
                    Promo Codes ({filteredPromos.length})
                  </MDTypography>
                  {canCreate && (
                    <MDButton
                      variant="contained"
                      onClick={() => navigate('/promocodes/create')}>
                      Create New Promo Code
                    </MDButton>
                  )}
                </MDBox>

                <Grid container spacing={2} p={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <MDInput
                      fullWidth
                      label="Search by code or description"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        label="Status">
                        {FILTERS.status.map(f => (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Scope</InputLabel>
                      <Select
                        value={scopeFilter}
                        onChange={e => setScopeFilter(e.target.value)}
                        label="Scope">
                        {FILTERS.scope.map(f => (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}
                        label="Type">
                        {FILTERS.type.map(f => (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={countryFilter}
                        onChange={e => setCountryFilter(e.target.value)}
                        label="Country">
                        {FILTERS.country.map(f => (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {filteredPromos.length === 0 && !loading && (
                  <MDBox textAlign="center" py={6}>
                    <Icon sx={{fontSize: 64, color: 'text.secondary', mb: 2}}>
                      local_offer
                    </Icon>
                    <MDTypography variant="h6" mb={1}>
                      {promos.length === 0
                        ? 'No Promo Codes Yet'
                        : 'No matching promos found'}
                    </MDTypography>
                    <MDTypography variant="body2" color="textSecondary" mb={3}>
                      {promos.length === 0
                        ? 'Create your first promo code to offer discounts to your customers.'
                        : 'Try adjusting your search or filters.'}
                    </MDTypography>
                    {promos.length === 0 && canCreate && (
                      <MDButton
                        variant="contained"
                        onClick={() => navigate('/promocodes/create')}>
                        Create Promo Code
                      </MDButton>
                    )}
                  </MDBox>
                )}

                {filteredPromos.length > 0 && (
                  <MDBox pt={3}>
                    {/*
                      IMPORTANT FIX:
                      The `key` prop below is intentional. Many versions of the
                      Creative Tim `DataTable` component memoize/initialize their
                      internal react-table state only once on mount and don't
                      react to prop changes on `table.rows`. That means after a
                      status toggle / delete / re-fetch, the parent's data
                      changes but the table visually stays stale until a full
                      page reload (new mount).
                      By keying the table on a fingerprint of the current rows
                      (ids + isActive flags), React will unmount + remount the
                      DataTable whenever the underlying data actually changes,
                      forcing it to re-initialize with fresh data instantly.
                    */}
                    <DataTable
                      key={filteredPromos
                        .map(p => `${p.id}-${p.isActive}-${p.usageCount}`)
                        .join('|')}
                      table={{
                        columns,
                        rows: filteredPromos.map((row, idx) =>
                          getRow(row, idx),
                        ),
                      }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={true}
                      noEndBorder
                      hoverEffect
                    />
                  </MDBox>
                )}
              </Card>
            </Grid>
            <Divider orientation="vertical" sx={{mx: 0}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Menu
        anchorEl={menu}
        open={Boolean(menu)}
        onClose={closeMenu}
        anchorOrigin={{vertical: 'top', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}>
        {canUpdate && (
          <MenuItem onClick={handleToggleStatus}>
            <Icon sx={{mr: 1}}>
              {selectedPromo?.isActive ? 'pause' : 'play_arrow'}
            </Icon>
            {selectedPromo?.isActive ? 'Deactivate' : 'Activate'} Promo
          </MenuItem>
        )}
        {canUpdate && (
          <MenuItem
            onClick={() => {
              navigate(`/promocodes/edit/${selectedPromo?.id}`);
              closeMenu();
            }}>
            <Icon sx={{mr: 1}}>edit</Icon>
            Edit Promo
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              handleDelete(selectedPromo?.id);
              closeMenu();
            }}>
            <Icon sx={{mr: 1}} color="error">
              delete
            </Icon>
            Delete Promo
          </MenuItem>
        )}
      </Menu>
    </DashboardLayout>
  );
};

export default PromoCodes;

// import {useEffect, useState} from 'react';
// import MDBox from 'components/MDBox';
// import MDTypography from 'components/MDTypography';
// import MDBadge from 'components/MDBadge';
// import DataTable from 'examples/Tables/DataTable';
// import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
// import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
// import Footer from 'examples/Footer';
// import {useSelector} from 'react-redux';
// import axios from 'axios';
// import {useNavigate} from 'react-router-dom';
// import MDInput from 'components/MDInput';
// import {ENDPOINT} from 'constants';
// import MDButton from 'components/MDButton';
// import {
//   Card,
//   CircularProgress,
//   Menu,
//   MenuItem,
//   Grid,
//   Divider,
//   Icon,
//   IconButton,
//   FormControl,
//   InputLabel,
//   Select,
//   Tooltip,
//   Box,
//   Typography,
// } from '@mui/material';
// import {Confirm, Loading, Notify} from 'notiflix';
// import moment from 'moment';
// import CountryDropdown from 'components/country/CountryDropdown';

// const FILTERS = {
//   status: [
//     {label: 'All Status', value: 'all'},
//     {label: 'Active', value: 'active'},
//     {label: 'Inactive', value: 'inactive'},
//     {label: 'Expired', value: 'expired'},
//   ],
//   scope: [
//     {label: 'All Scopes', value: 'all'},
//     {label: 'Global', value: 'global'},
//     {label: 'Customer Specific', value: 'customer-specific'},
//   ],
//   type: [
//     {label: 'All Types', value: 'all'},
//     {label: 'Percentage', value: 'percentage'},
//     {label: 'Flat Amount', value: 'flat'},
//   ],
//   country: [
//     {label: 'All Countries', value: 'all'},
//     {label: 'All Countries Scope', value: 'all-scope'},
//     {label: 'Specific Country', value: 'specific'},
//   ],
// };

// const columns = [
//   {Header: '#', accessor: 'sr', width: '5%', align: 'left'},
//   {Header: 'Promo Code', accessor: 'code', align: 'left'},
//   {Header: 'Discount', accessor: 'discount', align: 'left'},
//   {Header: 'Scope', accessor: 'scope', align: 'center'},
//   {Header: 'Usage', accessor: 'usageCount', align: 'center'},
//   {Header: 'Expiration', accessor: 'expirationDate', align: 'center'},
//   {Header: 'Status', accessor: 'status', align: 'center'},
//   {Header: 'Actions', accessor: 'actions', align: 'center'},
// ];

// const PromoCodes = () => {
//   const [promos, setPromos] = useState([]);
//   const [filteredPromos, setFilteredPromos] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [reRender, setReRender] = useState(false);

//   const [searchQuery, setSearchQuery] = useState('');
//   const [statusFilter, setStatusFilter] = useState('all');
//   const [scopeFilter, setScopeFilter] = useState('all');
//   const [typeFilter, setTypeFilter] = useState('all');
//   const [countryFilter, setCountryFilter] = useState('all');

//   const [menu, setMenu] = useState(null);
//   const [selectedPromo, setSelectedPromo] = useState(null);

//   const {user} = useSelector(state => state.auth);
//   const navigate = useNavigate();

//   useEffect(() => {
//     fetchPromos();
//   }, [reRender]);

//   useEffect(() => {
//     applyFilters();
//   }, [promos, searchQuery, statusFilter, scopeFilter, typeFilter, countryFilter]);

//   const fetchPromos = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const {data} = await axios.get(`${ENDPOINT}api/v1/promo-code/get-all`, {
//         headers: {Authorization: `${user?.accessToken}`},
//       });
//       setPromos(data?.data || []);
//     } catch (error) {
//       console.error('Error fetching promos:', error);
//       setError('Failed to load promo codes');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const applyFilters = () => {
//     let result = [...promos];

//     if (searchQuery) {
//       const query = searchQuery.toLowerCase();
//       result = result.filter(
//         p =>
//           p.code?.toLowerCase().includes(query) ||
//           p.description?.toLowerCase().includes(query),
//       );
//     }

//     if (statusFilter !== 'all') {
//       const now = moment();
//       result = result.filter(p => {
//         if (statusFilter === 'active') return p.isActive && moment(p.expirationDate).isAfter(now);
//         if (statusFilter === 'inactive') return !p.isActive;
//         if (statusFilter === 'expired') return moment(p.expirationDate).isBefore(now);
//         return true;
//       });
//     }

//     if (scopeFilter !== 'all') {
//       result = result.filter(p => p.promoScope === scopeFilter);
//     }

//     if (typeFilter !== 'all') {
//       result = result.filter(p => p.discountType === typeFilter);
//     }

//     if (countryFilter !== 'all') {
//       result = result.filter(p => {
//         if (countryFilter === 'all-scope') return p.countryScope === 'all';
//         if (countryFilter === 'specific') return p.countryScope === 'specific';
//         return true;
//       });
//     }

//     setFilteredPromos(result);
//   };

//   const openMenu = (event, promo) => {
//     setSelectedPromo(promo);
//     setMenu(event.currentTarget);
//   };
//   const closeMenu = () => {
//     setMenu(null);
//     setSelectedPromo(null);
//   };

//   const handleToggleStatus = async () => {
//     try {
//       Loading.circle('Changing Status...');
//       await axios.patch(
//         `${ENDPOINT}api/v1/promo-code/update`,
//         {
//           promoCodeOrId: selectedPromo.code,
//           isActive: !selectedPromo.isActive,
//         },
//         {headers: {Authorization: `Bearer ${user?.accessToken}`}},
//       );
//       Loading.remove();
//       Notify.success(`Promo ${selectedPromo.isActive ? 'deactivated' : 'activated'} successfully`);
//       setReRender(!reRender);
//     } catch (error) {
//       Loading.remove();
//       Notify.failure('Failed to update status');
//     }
//     closeMenu();
//   };

//   const handleDelete = id => {
//     const promo = promos.find(p => p.id === id);
//     const usageWarning =
//       promo?.usageCount > 0
//         ? `\n\n⚠️ This promo has been used ${promo.usageCount} times. Deleting it will remove it permanently but usage records will be preserved.`
//         : '';

//     Confirm.show(
//       'Delete Promo Code?',
//       `Are you sure you want to delete "${promo?.code}"?${usageWarning}\n\nConsider deactivating instead if you want to keep the history.`,
//       'Delete',
//       'Cancel',
//       async () => {
//         try {
//           Loading.circle('Deleting...');
//           await axios.delete(`${ENDPOINT}api/v1/promo-code/delete`, {
//             headers: {Authorization: `Bearer ${user?.accessToken}`},
//             data: {promoCodeOrId: id},
//           });
//           Loading.remove();
//           Notify.success('Promo code deleted');
//           setReRender(!reRender);
//         } catch (error) {
//           Loading.remove();
//           Notify.failure(error?.response?.data?.message || 'Failed to delete');
//         }
//       },
//       () => {},
//     );
//   };

//   const getRow = (row, idx) => ({
//     sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
//     code: (
//       <MDTypography
//         variant="button"
//         fontWeight="medium"
//         color="secondary"
//         sx={{cursor: 'pointer'}}
//         onClick={() => navigate(`/promocodes/edit/${row.id}`)}>
//         {row.code}
//       </MDTypography>
//     ),
//     discount: (
//       <MDTypography variant="caption">
//         {row.discountValue}
//         {row.discountType === 'percentage' ? '%' : '₹'}
//       </MDTypography>
//     ),
//     scope: (
//       <MDBox display="flex" gap={0.5} flexDirection="column">
//         <MDBadge
//           badgeContent={row.promoScope === 'global' ? 'All Users' : 'Specific Users'}
//           color={row.promoScope === 'global' ? 'info' : 'warning'}
//           variant="gradient"
//           size="sm"
//         />
//         <MDBadge
//           badgeContent={row.countryScope === 'all' ? 'All Countries' : 'Specific Country'}
//           color={row.countryScope === 'all' ? 'success' : 'error'}
//           variant="gradient"
//           size="sm"
//         />
//       </MDBox>
//     ),
//     usageCount: (
//       <MDTypography variant="caption">
//         {row.usageCount || 0} / {row.maximumNumberOfUsage}
//       </MDTypography>
//     ),
//     expirationDate: (
//       <MDTypography variant="caption">
//         {moment(row.expirationDate).format('DD MMM YYYY')}
//       </MDTypography>
//     ),
//     status: (
//       <MDBadge
//         badgeContent={
//           moment(row.expirationDate).isBefore(moment())
//             ? 'expired'
//             : row.isActive
//             ? 'active'
//             : 'inactive'
//         }
//         color={
//           moment(row.expirationDate).isBefore(moment())
//             ? 'error'
//             : row.isActive
//             ? 'success'
//             : 'secondary'
//         }
//         variant="gradient"
//         size="sm"
//       />
//     ),
//     actions: (
//       <MDBox display="flex" gap={1} justifyContent="center">
//         {/* <Tooltip title="Edit">
//           <IconButton size="small" onClick={() => navigate(`/promocodes/edit/${row.id}`)}>
//             <Icon fontSize="small">edit</Icon>
//           </IconButton>
//         </Tooltip> */}
//         <Tooltip title={row.isActive ? 'Deactivate' : 'Activate'}>
//           <IconButton
//             size="small"
//             onClick={e => {
//               setSelectedPromo(row);
//               openMenu(e, row);
//             }}>
//             <Icon fontSize="small">more_vert</Icon>
//           </IconButton>
//         </Tooltip>
//         {/* <Tooltip title="Delete">
//           <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
//             <Icon fontSize="small">delete</Icon>
//           </IconButton>
//         </Tooltip> */}
//       </MDBox>
//     ),
//   });

//   if (loading) {
//     return (
//       <DashboardLayout>
//         <DashboardNavbar />
//         <MDBox mt={5} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
//           <Box textAlign="center">
//             <CircularProgress size="3rem" color="inherit" />
//             <MDTypography variant="h6" mt={2}>Loading promo codes...</MDTypography>
//           </Box>
//         </MDBox>
//       </DashboardLayout>
//     );
//   }

//   if (error) {
//     return (
//       <DashboardLayout>
//         <DashboardNavbar />
//         <MDBox mt={5} mb={3}>
//           <Card sx={{padding: 4, textAlign: 'center'}}>
//             <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>error_outline</Icon>
//             <MDTypography variant="h5" mb={2}>{error}</MDTypography>
//             <MDButton variant="contained" onClick={() => { setError(null); fetchPromos(); }}>Retry</MDButton>
//           </Card>
//         </MDBox>
//       </DashboardLayout>
//     );
//   }

//   return (
//     <DashboardLayout>
//       <DashboardNavbar />
//       <MDBox mt={5} mb={3}>
//         <Grid container spacing={6}>
//           <Grid item xs={12} sx={{display: 'flex'}}>
//             <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
//             <Grid item xs={12}>
//               <Card>
//                 <MDBox
//                   mx={2}
//                   mt={-3}
//                   py={3}
//                   px={2}
//                   variant="gradient"
//                   bgColor="info"
//                   borderRadius="lg"
//                   coloredShadow="info"
//                   sx={{
//                     display: 'flex',
//                     justifyContent: 'space-between',
//                     alignItems: 'center',
//                     flexWrap: 'wrap',
//                     gap: 2,
//                   }}>
//                   <MDTypography variant="h6" color="white">
//                     Promo Codes ({filteredPromos.length})
//                   </MDTypography>
//                   <MDButton variant="contained" onClick={() => navigate('/promocodes/create')}>
//                     Create New Promo Code
//                   </MDButton>
//                 </MDBox>

//                 <Grid container spacing={2} p={2}>
//                   <Grid item xs={12} sm={6} md={3}>
//                     <MDInput
//                       fullWidth
//                       label="Search by code or description"
//                       value={searchQuery}
//                       onChange={e => setSearchQuery(e.target.value)}
//                     />
//                   </Grid>
//                   <Grid item xs={6} sm={3} md={2}>
//                     <FormControl fullWidth size="small">
//                       <InputLabel>Status</InputLabel>
//                       <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
//                         {FILTERS.status.map(f => (
//                           <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
//                         ))}
//                       </Select>
//                     </FormControl>
//                   </Grid>
//                   <Grid item xs={6} sm={3} md={2}>
//                     <FormControl fullWidth size="small">
//                       <InputLabel>Scope</InputLabel>
//                       <Select value={scopeFilter} onChange={e => setScopeFilter(e.target.value)} label="Scope">
//                         {FILTERS.scope.map(f => (
//                           <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
//                         ))}
//                       </Select>
//                     </FormControl>
//                   </Grid>
//                   <Grid item xs={6} sm={3} md={2}>
//                     <FormControl fullWidth size="small">
//                       <InputLabel>Type</InputLabel>
//                       <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} label="Type">
//                         {FILTERS.type.map(f => (
//                           <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
//                         ))}
//                       </Select>
//                     </FormControl>
//                   </Grid>
//                   <Grid item xs={6} sm={3} md={2}>
//                     <FormControl fullWidth size="small">
//                       <InputLabel>Country</InputLabel>
//                       <Select value={countryFilter} onChange={e => setCountryFilter(e.target.value)} label="Country">
//                         {FILTERS.country.map(f => (
//                           <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
//                         ))}
//                       </Select>
//                     </FormControl>
//                   </Grid>
//                 </Grid>

//                 {filteredPromos.length === 0 && !loading && (
//                   <MDBox textAlign="center" py={6}>
//                     <Icon sx={{fontSize: 64, color: 'text.secondary', mb: 2}}>local_offer</Icon>
//                     <MDTypography variant="h6" mb={1}>
//                       {promos.length === 0 ? 'No Promo Codes Yet' : 'No matching promos found'}
//                     </MDTypography>
//                     <MDTypography variant="body2" color="textSecondary" mb={3}>
//                       {promos.length === 0
//                         ? 'Create your first promo code to offer discounts to your customers.'
//                         : 'Try adjusting your search or filters.'}
//                     </MDTypography>
//                     {promos.length === 0 && (
//                       <MDButton variant="contained" onClick={() => navigate('/promocodes/create')}>
//                         Create Promo Code
//                       </MDButton>
//                     )}
//                   </MDBox>
//                 )}

//                 {filteredPromos.length > 0 && (
//                   <MDBox pt={3}>
//                     <DataTable
//                       table={{
//                         columns,
//                         rows: filteredPromos.map((row, idx) => getRow(row, idx)),
//                       }}
//                       isSorted={false}
//                       entriesPerPage={false}
//                       showTotalEntries={true}
//                       noEndBorder
//                       hoverEffect
//                     />
//                   </MDBox>
//                 )}
//               </Card>
//             </Grid>
//             <Divider orientation="vertical" sx={{mx: 0}} />
//           </Grid>
//         </Grid>
//       </MDBox>
//       <Footer />

//       <Menu
//         anchorEl={menu}
//         open={Boolean(menu)}
//         onClose={closeMenu}
//         anchorOrigin={{vertical: 'top', horizontal: 'left'}}
//         transformOrigin={{vertical: 'top', horizontal: 'right'}}>
//         <MenuItem onClick={handleToggleStatus}>
//           <Icon sx={{mr: 1}}>{selectedPromo?.isActive ? 'pause' : 'play_arrow'}</Icon>
//           {selectedPromo?.isActive ? 'Deactivate' : 'Activate'} Promo
//         </MenuItem>
//         <MenuItem onClick={() => { navigate(`/promocodes/edit/${selectedPromo?.id}`); closeMenu(); }}>
//           <Icon sx={{mr: 1}}>edit</Icon>
//           Edit Promo
//         </MenuItem>
//         <MenuItem onClick={() => { handleDelete(selectedPromo?.id); closeMenu(); }}>
//           <Icon sx={{mr: 1}} color="error">delete</Icon>
//           Delete Promo
//         </MenuItem>
//       </Menu>
//     </DashboardLayout>
//   );
// };

// export default PromoCodes;

// // import {useEffect, useState} from 'react';
// // import MDBox from 'components/MDBox';
// // import MDTypography from 'components/MDTypography';
// // import MDBadge from 'components/MDBadge';
// // import DataTable from 'examples/Tables/DataTable';
// // import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
// // import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
// // import Footer from 'examples/Footer';
// // import {useSelector} from 'react-redux';
// // import axios from 'axios';
// // import {useNavigate} from 'react-router-dom';
// // import MDInput from 'components/MDInput';
// // import {ENDPOINT} from 'constants';
// // import MDButton from 'components/MDButton';
// // import {
// //   Card,
// //   CircularProgress,
// //   Menu,
// //   MenuItem,
// //   Grid,
// //   Divider,
// //   Icon,
// //   IconButton,
// //   Checkbox,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   Tooltip,
// //   Chip,
// //   Alert,
// //   Box,
// //   Typography,
// // } from '@mui/material';
// // import {Confirm, Loading, Notify} from 'notiflix';
// // import moment from 'moment';

// // const FILTERS = {
// //   status: [
// //     {label: 'All Status', value: 'all'},
// //     {label: 'Active', value: 'active'},
// //     {label: 'Inactive', value: 'inactive'},
// //     {label: 'Expired', value: 'expired'},
// //   ],
// //   scope: [
// //     {label: 'All Scopes', value: 'all'},
// //     {label: 'Global', value: 'global'},
// //     {label: 'Customer Specific', value: 'customer-specific'},
// //   ],
// //   type: [
// //     {label: 'All Types', value: 'all'},
// //     {label: 'Percentage', value: 'percentage'},
// //     {label: 'Flat Amount', value: 'flat'},
// //   ],
// // };

// // const getColumns = (handleSelectAll, isAllSelected) => [
// //   {
// //     Header: () => (
// //       <Checkbox
// //         checked={isAllSelected}
// //         onChange={e => handleSelectAll(e.target.checked)}
// //         size="small"
// //       />
// //     ),
// //     accessor: 'select',
// //     width: '3%',
// //     align: 'center',
// //   },
// //   {Header: '#', accessor: 'sr', width: '5%', align: 'left'},
// //   {Header: 'Promo Code', accessor: 'code', align: 'left'},
// //   {Header: 'Discount', accessor: 'discount', align: 'left'},
// //   {Header: 'Scope', accessor: 'scope', align: 'center'},
// //   {Header: 'Usage', accessor: 'usageCount', align: 'center'},
// //   {Header: 'Expiration', accessor: 'expirationDate', align: 'center'},
// //   {Header: 'Status', accessor: 'status', align: 'center'},
// //   {Header: 'Actions', accessor: 'actions', align: 'center'},
// // ];

// // const PromoCodes = () => {
// //   const [promos, setPromos] = useState([]);
// //   const [filteredPromos, setFilteredPromos] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [error, setError] = useState(null);
// //   const [reRender, setReRender] = useState(false);

// //   // Filters
// //   const [searchQuery, setSearchQuery] = useState('');
// //   const [statusFilter, setStatusFilter] = useState('all');
// //   const [scopeFilter, setScopeFilter] = useState('all');
// //   const [typeFilter, setTypeFilter] = useState('all');

// //   // Selection
// //   const [selectedIds, setSelectedIds] = useState([]);
// //   const [menu, setMenu] = useState(null);
// //   const [selectedPromo, setSelectedPromo] = useState(null);

// //   const {user} = useSelector(state => state.auth);
// //   const navigate = useNavigate();

// //   // Fetch promos
// //   useEffect(() => {
// //     fetchPromos();
// //   }, [reRender]);

// //   // Apply filters
// //   useEffect(() => {
// //     applyFilters();
// //   }, [promos, searchQuery, statusFilter, scopeFilter, typeFilter]);

// //   const fetchPromos = async () => {
// //     setLoading(true);
// //     setError(null);
// //     try {
// //       const {data} = await axios.get(`${ENDPOINT}api/v1/promo-code/get-all`, {
// //         headers: {Authorization: `${user?.accessToken}`},
// //       });
// //       setPromos(data?.data || []);
// //     } catch (error) {
// //       console.error('Error fetching promos:', error);
// //       setError('Failed to load promo codes');
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const applyFilters = () => {
// //     let result = [...promos];

// //     // Search by code or description
// //     if (searchQuery) {
// //       const query = searchQuery.toLowerCase();
// //       result = result.filter(
// //         p =>
// //           p.code?.toLowerCase().includes(query) ||
// //           p.description?.toLowerCase().includes(query),
// //       );
// //     }

// //     // Status filter
// //     if (statusFilter !== 'all') {
// //       const now = moment();
// //       result = result.filter(p => {
// //         if (statusFilter === 'active')
// //           return p.isActive && moment(p.expirationDate).isAfter(now);
// //         if (statusFilter === 'inactive') return !p.isActive;
// //         if (statusFilter === 'expired')
// //           return moment(p.expirationDate).isBefore(now);
// //         return true;
// //       });
// //     }

// //     // Scope filter
// //     if (scopeFilter !== 'all') {
// //       result = result.filter(p => p.promoScope === scopeFilter);
// //     }

// //     // Type filter
// //     if (typeFilter !== 'all') {
// //       result = result.filter(p => p.discountType === typeFilter);
// //     }

// //     setFilteredPromos(result);
// //   };

// //   // Selection handlers
// //   const handleSelectAll = checked => {
// //     if (checked) {
// //       setSelectedIds(filteredPromos.map(p => p.id));
// //     } else {
// //       setSelectedIds([]);
// //     }
// //   };

// //   const handleSelectOne = id => {
// //     setSelectedIds(prev =>
// //       prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
// //     );
// //   };

// //   const isAllSelected =
// //     filteredPromos.length > 0 && selectedIds.length === filteredPromos.length;

// //   // Menu handlers
// //   const openMenu = (event, promo) => {
// //     setSelectedPromo(promo);
// //     setMenu(event.currentTarget);
// //   };
// //   const closeMenu = () => {
// //     setMenu(null);
// //     setSelectedPromo(null);
// //   };

// //   // Action handlers
// //   const handleToggleStatus = async () => {
// //     try {
// //       Loading.circle('Changing Status...');
// //       await axios.patch(
// //         `${ENDPOINT}api/v1/promo-code/update`,
// //         {
// //           promoCodeOrId: selectedPromo.code,
// //           isActive: !selectedPromo.isActive,
// //         },
// //         {headers: {Authorization: `Bearer ${user?.accessToken}`}},
// //       );
// //       Loading.remove();
// //       Notify.success(
// //         `Promo ${
// //           selectedPromo.isActive ? 'deactivated' : 'activated'
// //         } successfully`,
// //       );
// //       setReRender(!reRender);
// //     } catch (error) {
// //       Loading.remove();
// //       Notify.failure('Failed to update status');
// //     }
// //     closeMenu();
// //   };

// //   const handleDelete = id => {
// //     const promo = promos.find(p => p.id === id);
// //     const usageWarning =
// //       promo?.usageCount > 0
// //         ? `\n\n⚠️ This promo has been used ${promo.usageCount} times. Deleting it will remove it permanently but usage records will be preserved.`
// //         : '';

// //     Confirm.show(
// //       'Delete Promo Code?',
// //       `Are you sure you want to delete "${promo?.code}"?${usageWarning}\n\nConsider deactivating instead if you want to keep the history.`,
// //       'Delete',
// //       'Cancel',
// //       async () => {
// //         try {
// //           Loading.circle('Deleting...');
// //           await axios.delete(`${ENDPOINT}api/v1/promo-code/delete`, {
// //             headers: {Authorization: `Bearer ${user?.accessToken}`},
// //             data: {promoCodeOrId: id},
// //           });
// //           Loading.remove();
// //           Notify.success('Promo code deleted');
// //           setReRender(!reRender);
// //         } catch (error) {
// //           Loading.remove();
// //           Notify.failure(error?.response?.data?.message || 'Failed to delete');
// //         }
// //       },
// //       () => {},
// //     );
// //   };

// //   const handleBulkDelete = () => {
// //     if (selectedIds.length === 0) {
// //       Notify.warning('Select promos to delete');
// //       return;
// //     }
// //     Confirm.show(
// //       'Delete Selected Promos?',
// //       `You are about to delete ${selectedIds.length} promo code(s). This action cannot be undone.`,
// //       'Delete All',
// //       'Cancel',
// //       async () => {
// //         try {
// //           Loading.circle(`Deleting ${selectedIds.length} promos...`);
// //           await Promise.all(
// //             selectedIds.map(id =>
// //               axios.delete(`${ENDPOINT}api/v1/promo-code/delete`, {
// //                 headers: {Authorization: `Bearer ${user?.accessToken}`},
// //                 data: {promoCodeOrId: id},
// //               }),
// //             ),
// //           );
// //           Loading.remove();
// //           Notify.success(`${selectedIds.length} promos deleted`);
// //           setSelectedIds([]);
// //           setReRender(!reRender);
// //         } catch (error) {
// //           Loading.remove();
// //           Notify.failure('Failed to delete some promos');
// //         }
// //       },
// //       () => {},
// //     );
// //   };

// //   const handleBulkDeactivate = async () => {
// //     if (selectedIds.length === 0) {
// //       Notify.warning('Select promos to deactivate');
// //       return;
// //     }
// //     try {
// //       Loading.circle(`Deactivating ${selectedIds.length} promos...`);
// //       await Promise.all(
// //         selectedIds.map(id =>
// //           axios.patch(
// //             `${ENDPOINT}api/v1/promo-code/update`,
// //             {promoCodeOrId: id, isActive: false},
// //             {headers: {Authorization: `Bearer ${user?.accessToken}`}},
// //           ),
// //         ),
// //       );
// //       Loading.remove();
// //       Notify.success(`${selectedIds.length} promos deactivated`);
// //       setSelectedIds([]);
// //       setReRender(!reRender);
// //     } catch (error) {
// //       Loading.remove();
// //       Notify.failure('Failed to deactivate some promos');
// //     }
// //   };

// //   // Get row data
// //   const getRow = (row, idx) => ({
// //     select: (
// //       <Checkbox
// //         checked={selectedIds.includes(row.id)}
// //         onChange={() => handleSelectOne(row.id)}
// //         size="small"
// //       />
// //     ),
// //     sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
// //     code: (
// //       <MDTypography
// //         variant="button"
// //         fontWeight="medium"
// //         color="secondary"
// //         sx={{cursor: 'pointer'}}
// //         onClick={() => navigate(`/promocodes/edit/${row.id}`)}>
// //         {row.code}
// //       </MDTypography>
// //     ),
// //     discount: (
// //       <MDTypography variant="caption">
// //         {row.discountValue}
// //         {row.discountType === 'percentage' ? '%' : '₹'}
// //       </MDTypography>
// //     ),
// //     scope: (
// //       <MDBadge
// //         badgeContent={row.promoScope === 'global' ? 'Global' : 'Specific'}
// //         color={row.promoScope === 'global' ? 'info' : 'warning'}
// //         variant="gradient"
// //         size="sm"
// //       />
// //     ),
// //     usageCount: (
// //       <MDTypography variant="caption">
// //         {row.usageCount || 0} / {row.maximumNumberOfUsage}
// //       </MDTypography>
// //     ),
// //     expirationDate: (
// //       <MDTypography variant="caption">
// //         {moment(row.expirationDate).format('DD MMM YYYY')}
// //       </MDTypography>
// //     ),
// //     status: (
// //       <MDBadge
// //         badgeContent={
// //           moment(row.expirationDate).isBefore(moment())
// //             ? 'expired'
// //             : row.isActive
// //             ? 'active'
// //             : 'inactive'
// //         }
// //         color={
// //           moment(row.expirationDate).isBefore(moment())
// //             ? 'error'
// //             : row.isActive
// //             ? 'success'
// //             : 'secondary'
// //         }
// //         variant="gradient"
// //         size="sm"
// //       />
// //     ),
// //     actions: (
// //       <MDBox display="flex" gap={1} justifyContent="center">
// //         <Tooltip title="Edit">
// //           <IconButton
// //             size="small"
// //             onClick={() => navigate(`/promocodes/edit/${row.id}`)}>
// //             <Icon fontSize="small">edit</Icon>
// //           </IconButton>
// //         </Tooltip>
// //         <Tooltip title={row.isActive ? 'Deactivate' : 'Activate'}>
// //           <IconButton
// //             size="small"
// //             onClick={e => {
// //               setSelectedPromo(row);
// //               openMenu(e, row);
// //             }}>
// //             <Icon fontSize="small">more_vert</Icon>
// //           </IconButton>
// //         </Tooltip>
// //         <Tooltip title="Delete">
// //           <IconButton
// //             size="small"
// //             color="error"
// //             onClick={() => handleDelete(row.id)}>
// //             <Icon fontSize="small">delete</Icon>
// //           </IconButton>
// //         </Tooltip>
// //       </MDBox>
// //     ),
// //   });

// //   // Render
// //   if (loading) {
// //     return (
// //       <DashboardLayout>
// //         <DashboardNavbar />
// //         <MDBox
// //           mt={5}
// //           display="flex"
// //           justifyContent="center"
// //           alignItems="center"
// //           minHeight="400px">
// //           <Box textAlign="center">
// //             <CircularProgress size="3rem" color="inherit" />
// //             <MDTypography variant="h6" mt={2}>
// //               Loading promo codes...
// //             </MDTypography>
// //           </Box>
// //         </MDBox>
// //       </DashboardLayout>
// //     );
// //   }

// //   if (error) {
// //     return (
// //       <DashboardLayout>
// //         <DashboardNavbar />
// //         <MDBox mt={5} mb={3}>
// //           <Card sx={{padding: 4, textAlign: 'center'}}>
// //             <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>
// //               error_outline
// //             </Icon>
// //             <MDTypography variant="h5" mb={2}>
// //               {error}
// //             </MDTypography>
// //             <MDButton
// //               variant="contained"
// //               onClick={() => {
// //                 setError(null);
// //                 fetchPromos();
// //               }}>
// //               Retry
// //             </MDButton>
// //           </Card>
// //         </MDBox>
// //       </DashboardLayout>
// //     );
// //   }

// //   return (
// //     <DashboardLayout>
// //       <DashboardNavbar />
// //       <MDBox mt={5} mb={3}>
// //         <Grid container spacing={6}>
// //           <Grid item xs={12} sx={{display: 'flex'}}>
// //             <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
// //             <Grid item xs={12}>
// //               <Card>
// //                 {/* Header */}
// //                 <MDBox
// //                   mx={2}
// //                   mt={-3}
// //                   py={3}
// //                   px={2}
// //                   variant="gradient"
// //                   bgColor="info"
// //                   borderRadius="lg"
// //                   coloredShadow="info"
// //                   sx={{
// //                     display: 'flex',
// //                     justifyContent: 'space-between',
// //                     alignItems: 'center',
// //                     flexWrap: 'wrap',
// //                     gap: 2,
// //                   }}>
// //                   <MDTypography variant="h6" color="white">
// //                     Promo Codes ({filteredPromos.length})
// //                   </MDTypography>
// //                   <MDButton
// //                     variant="contained"
// //                     onClick={() => navigate('/promocodes/create')}>
// //                     Create New Promo Code
// //                   </MDButton>
// //                 </MDBox>

// //                 {/* Filters */}
// //                 <Grid container spacing={2} p={2}>
// //                   <Grid item xs={12} sm={6} md={3}>
// //                     <MDInput
// //                       fullWidth
// //                       label="Search by code or description"
// //                       value={searchQuery}
// //                       onChange={e => setSearchQuery(e.target.value)}
// //                     />
// //                   </Grid>
// //                   <Grid item xs={6} sm={3} md={2}>
// //                     <FormControl fullWidth size="small">
// //                       <InputLabel>Status</InputLabel>
// //                       <Select
// //                         value={statusFilter}
// //                         onChange={e => setStatusFilter(e.target.value)}
// //                         label="Status">
// //                         {FILTERS.status.map(f => (
// //                           <MenuItem key={f.value} value={f.value}>
// //                             {f.label}
// //                           </MenuItem>
// //                         ))}
// //                       </Select>
// //                     </FormControl>
// //                   </Grid>
// //                   <Grid item xs={6} sm={3} md={2}>
// //                     <FormControl fullWidth size="small">
// //                       <InputLabel>Scope</InputLabel>
// //                       <Select
// //                         value={scopeFilter}
// //                         onChange={e => setScopeFilter(e.target.value)}
// //                         label="Scope">
// //                         {FILTERS.scope.map(f => (
// //                           <MenuItem key={f.value} value={f.value}>
// //                             {f.label}
// //                           </MenuItem>
// //                         ))}
// //                       </Select>
// //                     </FormControl>
// //                   </Grid>
// //                   <Grid item xs={6} sm={3} md={2}>
// //                     <FormControl fullWidth size="small">
// //                       <InputLabel>Type</InputLabel>
// //                       <Select
// //                         value={typeFilter}
// //                         onChange={e => setTypeFilter(e.target.value)}
// //                         label="Type">
// //                         {FILTERS.type.map(f => (
// //                           <MenuItem key={f.value} value={f.value}>
// //                             {f.label}
// //                           </MenuItem>
// //                         ))}
// //                       </Select>
// //                     </FormControl>
// //                   </Grid>
// //                 </Grid>

// //                 {/* Bulk Actions */}
// //                 {selectedIds.length > 0 && (
// //                   <MDBox
// //                     px={2}
// //                     pb={1}
// //                     display="flex"
// //                     gap={2}
// //                     alignItems="center">
// //                     <Chip
// //                       label={`${selectedIds.length} selected`}
// //                       color="primary"
// //                       size="small"
// //                     />
// //                     <MDButton
// //                       size="small"
// //                       color="warning"
// //                       onClick={handleBulkDeactivate}>
// //                       Deactivate Selected
// //                     </MDButton>
// //                     <MDButton
// //                       size="small"
// //                       color="error"
// //                       onClick={handleBulkDelete}>
// //                       Delete Selected
// //                     </MDButton>
// //                     <MDButton
// //                       size="small"
// //                       color="secondary"
// //                       onClick={() => setSelectedIds([])}>
// //                       Clear Selection
// //                     </MDButton>
// //                   </MDBox>
// //                 )}

// //                 {/* Empty State */}
// //                 {filteredPromos.length === 0 && !loading && (
// //                   <MDBox textAlign="center" py={6}>
// //                     <Icon sx={{fontSize: 64, color: 'text.secondary', mb: 2}}>
// //                       local_offer
// //                     </Icon>
// //                     <MDTypography variant="h6" mb={1}>
// //                       {promos.length === 0
// //                         ? 'No Promo Codes Yet'
// //                         : 'No matching promos found'}
// //                     </MDTypography>
// //                     <MDTypography variant="body2" color="textSecondary" mb={3}>
// //                       {promos.length === 0
// //                         ? 'Create your first promo code to offer discounts to your customers.'
// //                         : 'Try adjusting your search or filters.'}
// //                     </MDTypography>
// //                     {promos.length === 0 && (
// //                       <MDButton
// //                         variant="contained"
// //                         onClick={() => navigate('/promocodes/create')}>
// //                         Create Promo Code
// //                       </MDButton>
// //                     )}
// //                   </MDBox>
// //                 )}

// //                 {/* Table */}
// //                 {filteredPromos.length > 0 && (
// //                   <MDBox pt={3}>
// //                     <DataTable
// //                       table={{
// //                         columns: getColumns(handleSelectAll, isAllSelected),
// //                         rows: filteredPromos.map((row, idx) =>
// //                           getRow(row, idx),
// //                         ),
// //                       }}
// //                       isSorted={false}
// //                       entriesPerPage={false}
// //                       showTotalEntries={true}
// //                       noEndBorder
// //                       hoverEffect
// //                     />
// //                   </MDBox>
// //                 )}
// //               </Card>
// //             </Grid>
// //             <Divider orientation="vertical" sx={{mx: 0}} />
// //           </Grid>
// //         </Grid>
// //       </MDBox>
// //       <Footer />

// //       {/* Context Menu */}
// //       <Menu
// //         anchorEl={menu}
// //         open={Boolean(menu)}
// //         onClose={closeMenu}
// //         anchorOrigin={{vertical: 'top', horizontal: 'left'}}
// //         transformOrigin={{vertical: 'top', horizontal: 'right'}}>
// //         <MenuItem onClick={handleToggleStatus}>
// //           <Icon sx={{mr: 1}}>
// //             {selectedPromo?.isActive ? 'pause' : 'play_arrow'}
// //           </Icon>
// //           {selectedPromo?.isActive ? 'Deactivate' : 'Activate'} Promo
// //         </MenuItem>
// //         <MenuItem
// //           onClick={() => {
// //             navigate(`/promocodes/edit/${selectedPromo?.id}`);
// //             closeMenu();
// //           }}>
// //           <Icon sx={{mr: 1}}>edit</Icon>
// //           Edit Promo
// //         </MenuItem>
// //         <MenuItem
// //           onClick={() => {
// //             handleDelete(selectedPromo?.id);
// //             closeMenu();
// //           }}>
// //           <Icon sx={{mr: 1}} color="error">
// //             delete
// //           </Icon>
// //           Delete Promo
// //         </MenuItem>
// //       </Menu>
// //     </DashboardLayout>
// //   );
// // };

// // export default PromoCodes;
